// Powerlete analytics helper (added 2026-11-04, D37 follow-up). The one way theme JS publishes a
// Shopify custom event. scripts/qa/check-analytics-publish-timing.mjs fails CI on a direct
// Shopify.analytics.publish call anywhere else.
//
// Trap 1, load order. Cloudflare Rocket Loader rewrites Shopify's web-pixels-manager setup script
// (it is not data-cfasync), so window.Shopify.analytics only exists once Rocket Loader replays
// scripts, about 100 ms after DOMContentLoaded. Theme modules are data-cfasync="false" and run first.
// A direct `Shopify.analytics?.publish?.()` at load is skipped without a sound: measured on live,
// empty_collection_view never reached the pixel. publish() waits for it (about 10 s at most).
//
// Trap 2, navigation. GA4 inside the sandboxed custom pixel holds hits for about 5 s and does not
// send them when the page unloads, so an event published by a click that leaves the page never
// reaches GA4 (measured: delivered to the pixel and GTM, no g/collect). publishOnNextPage() keeps it
// in sessionStorage and this module, loaded on every page by layout/theme.liquid, publishes it on the
// next page, where nothing is navigating.

const STASH_KEY = 'pw:ecs-click'; // one slot: a click can only start one navigation
const STASH_MAX_AGE_MS = 30000;

const ready = () => typeof window.Shopify?.analytics?.publish === 'function';

/**
 * Publishes a custom event as soon as Shopify's analytics API exists. Never throws.
 * @param {string} name
 * @param {object} data
 */
export function publish(name, data) {
  const send = () => {
    try {
      window.Shopify.analytics.publish(name, data);
    } catch (_) {
      // Analytics must never break the page.
    }
  };
  if (ready()) return send();
  let tries = 0;
  const timer = setInterval(() => {
    if (ready()) {
      clearInterval(timer);
      send();
    } else if (++tries >= 100) {
      clearInterval(timer);
    }
  }, 100);
}

/**
 * For an interaction that is about to leave this page: publish it on the next page instead.
 * @param {string} name
 * @param {object} data
 */
export function publishOnNextPage(name, data) {
  try {
    sessionStorage.setItem(STASH_KEY, JSON.stringify({ name, data, ts: Date.now() }));
  } catch (_) {
    publish(name, data); // storage blocked: best effort on this page
  }
}

function replayStashed() {
  let saved = null;
  try {
    const raw = sessionStorage.getItem(STASH_KEY);
    if (!raw) return;
    sessionStorage.removeItem(STASH_KEY);
    saved = JSON.parse(raw);
  } catch (_) {
    return;
  }
  // Older than 30 s means the navigation never happened (or the visitor went elsewhere first).
  if (!saved || typeof saved.name !== 'string' || !(Date.now() - saved.ts < STASH_MAX_AGE_MS)) return;
  publish(saved.name, saved.data);
}

replayStashed();
